const API_BASE = window.location.hostname === "localhost"
  ? "http://localhost:8001/api"
  : "https://api.maliajanda.com/api";

function getToken(): string | null {
  return localStorage.getItem("token");
}

export function setToken(token: string) {
  localStorage.setItem("token", token);
}

export function setRefreshToken(token: string) {
  localStorage.setItem("refresh_token", token);
}

export function clearAuth() {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
}

export function getUser() {
  const u = localStorage.getItem("user");
  return u ? JSON.parse(u) : null;
}

export function setUser(user: any) {
  localStorage.setItem("user", JSON.stringify(user));
}

async function request(path: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: any = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearAuth();
    window.location.href = "/auth/sign-in";
    throw new Error("Oturum suresi doldu");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Bir hata olustu" }));
    throw new Error(err.detail || "Bir hata olustu");
  }

  if (res.status === 204) return null;
  return res.json();
}

// Auth
export const api = {
  login: (email: string, password: string) =>
    request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, device_id: "admin-web", platform: "web" }),
    }),

  // Admin - Dashboard
  getStats: () => request("/admin/stats"),

  // Admin - Lisanslar
  getLicenses: (status?: string) =>
    request(`/admin/licenses${status ? `?status=${status}` : ""}`),
  createLicense: (data: any) =>
    request("/admin/licenses", { method: "POST", body: JSON.stringify(data) }),
  createLicensesBulk: (data: any) =>
    request("/admin/licenses/bulk", { method: "POST", body: JSON.stringify(data) }),
  getLicense: (id: number) => request(`/admin/licenses/${id}`),
  updateLicense: (id: number, data: any) =>
    request(`/admin/licenses/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteLicense: (id: number) =>
    request(`/admin/licenses/${id}`, { method: "DELETE" }),
  removeDevice: (licenseId: number, deviceId: number) =>
    request(`/admin/licenses/${licenseId}/devices/${deviceId}`, { method: "DELETE" }),

  // Admin - Kullanicilar
  getUsers: () => request("/admin/users"),
  getPendingUsers: () => request("/admin/users/pending"),
  createUser: (data: any) =>
    request("/admin/users", { method: "POST", body: JSON.stringify(data) }),
  approveUser: (id: number, data: any) =>
    request(`/admin/users/${id}/approve`, { method: "POST", body: JSON.stringify(data) }),
  rejectUser: (id: number) =>
    request(`/admin/users/${id}/reject`, { method: "POST" }),
  updateUser: (id: number, data: any) =>
    request(`/admin/users/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteUser: (id: number) =>
    request(`/admin/users/${id}`, { method: "DELETE" }),
  toggleUserActive: (id: number) =>
    request(`/admin/users/${id}/toggle-active`, { method: "PUT" }),
  extendLicense: (userId: number, data: any) =>
    request(`/admin/users/${userId}/extend-license`, { method: "POST", body: JSON.stringify(data) }),

  // Admin - Push debug
  pushDebug: () => request("/admin/push-debug"),
  pushTest: () => request("/admin/push-test", { method: "POST" }),
  pushVergiTakvimi: (when: "today" | "tomorrow" | "both" = "both") =>
    request(`/admin/push-vergi-takvimi?when=${when}`, { method: "POST" }),

  // Admin - Vergi Takvimi
  getVergiTakvimi: () => request("/admin/vergi-takvimi"),
  createVergi: (data: any) =>
    request("/admin/vergi-takvimi", { method: "POST", body: JSON.stringify(data) }),
  updateVergi: (id: number, data: any) =>
    request(`/admin/vergi-takvimi/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteVergi: (id: number) =>
    request(`/admin/vergi-takvimi/${id}`, { method: "DELETE" }),

  // Admin - Duyurular
  getDuyurular: () => request("/admin/duyurular"),
  createDuyuru: (data: any) =>
    request("/admin/duyurular", { method: "POST", body: JSON.stringify(data) }),
  updateDuyuru: (id: number, data: any) =>
    request(`/admin/duyurular/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteDuyuru: (id: number) =>
    request(`/admin/duyurular/${id}`, { method: "DELETE" }),

  // Admin - Content
  getContent: () => request("/admin/content"),
  createContent: (data: any) =>
    request("/admin/content", { method: "POST", body: JSON.stringify(data) }),
  updateContent: (id: number, data: any) =>
    request(`/admin/content/${id}`, { method: "PUT", body: JSON.stringify(data) }),

  // Admin - Faydali Linkler
  getLinks: () => request("/admin/links"),
  createLink: (data: any) =>
    request("/admin/links", { method: "POST", body: JSON.stringify(data) }),
  updateLink: (id: number, data: any) =>
    request(`/admin/links/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteLink: (id: number) =>
    request(`/admin/links/${id}`, { method: "DELETE" }),

  // Admin - Ayarlar
  getSettings: () => request("/admin/settings"),
  createSetting: (data: any) =>
    request("/admin/settings", { method: "POST", body: JSON.stringify(data) }),
  updateSetting: (id: number, data: any) =>
    request(`/admin/settings/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteSetting: (id: number) =>
    request(`/admin/settings/${id}`, { method: "DELETE" }),

  // Feedback / Destek
  feedbackStats: () => request("/admin/feedback/stats"),
  feedbackThreads: (status?: string) => request(`/admin/feedback/threads${status ? `?status=${status}` : ""}`),
  feedbackDetail: (threadId: number) => request(`/admin/feedback/threads/${threadId}`),
  feedbackReply: (threadId: number, body: string) =>
    request(`/admin/feedback/threads/${threadId}/reply`, { method: "POST", body: JSON.stringify({ body }) }),
  feedbackClose: (threadId: number) =>
    request(`/admin/feedback/threads/${threadId}/close`, { method: "POST" }),
  feedbackReopen: (threadId: number) =>
    request(`/admin/feedback/threads/${threadId}/reopen`, { method: "POST" }),
};
